/**
 * Site labels — Asana 1215600956750510, Figma node 4720-17696.
 *
 * A site carries at most one label: "Site Canceled", "Saturday Only" or
 * "Sunday Only". There is no label field in Airtable; all three are derived
 * from fields that already exist on Organizations:
 *
 *   - `Cancelled` (checkbox)  -> Site Canceled
 *   - `Saturday Hours` / `Sunday Hours` (free text) -> the day-only labels
 *
 * The hours fields are single-line TEXT, not a structured value, and closure is
 * written inconsistently: across the 202 confirmed sites they read "Closed" (26),
 * "CLOSED" (9), "closed" (6) and "n/a" (2). Hence the case-insensitive match
 * rather than a plain equality check.
 *
 * A BLANK value is deliberately NOT treated as closed. It means nobody has
 * filled the hours in yet, which is not the same as the site being shut, and
 * labelling a site "Sunday Only" because its Saturday hours are merely missing
 * would be worse than showing no label at all. No confirmed site is currently
 * blank, so this costs nothing today and protects against bad data later.
 */
export interface SiteLabel {
    text: string;
    /** `closed` renders red with the alert icon; `day` renders turquoise. */
    tone: 'closed' | 'day';
}

// Deliberately requires an actual word — an empty string does not match.
const CLOSED = /^\s*(closed|n\/?a|none|-|—)\s*$/i;

/**
 * Does this hours value mean "shut that day"?
 *
 * Exported because the day filter on the sites page has to agree with the
 * label: it used to test `value.toLowerCase() !== 'closed'`, which let four
 * sites through that this function calls closed — two written "n/a", and two
 * written "Closed " / "CLOSED " with a trailing space. Filtering to Sunday
 * therefore returned sites badged "Saturday Only" (Asana O2-8).
 */
export const isClosedHours = (value: unknown): boolean => CLOSED.test(String(value ?? ''));

const isClosed = isClosedHours;

export const useSiteLabel = (site: any): SiteLabel | null => {
    const fields = site?.fields;
    if (!fields) return null;

    // Cancelled wins: a cancelled site is shut regardless of its listed hours.
    // NOTE the spelling: the Airtable field is `Cancelled` (two Ls) but the
    // label Christine asked for is "Canceled" (one L) — Asana O2-38. The
    // mismatch is deliberate; don't "correct" either one to match the other.
    if (fields['Cancelled']) return { text: 'Site Canceled', tone: 'closed' };

    const saturdayClosed = isClosed(fields['Saturday Hours']);
    const sundayClosed = isClosed(fields['Sunday Hours']);

    // Shut both days without the Cancelled box ticked is contradictory data, so
    // say nothing rather than invent a label for it.
    if (saturdayClosed && sundayClosed) return null;

    if (sundayClosed) return { text: 'Saturday Only', tone: 'day' };
    if (saturdayClosed) return { text: 'Sunday Only', tone: 'day' };

    return null;
};
